/**
 * Catalog right pane — single component that handles four URL-driven modes:
 *
 * - article view+edit (URL: ``/catalog/articles/{id}``)
 * - article create (URL: ``/catalog/articles/new``)
 * - category create (URL: ``/catalog/categories/new``)
 * - product-group create (URL: ``/catalog/groups/new``)
 *
 * The catalog table compresses to ``Name + currently-sorted column`` (or
 * Name alone) whenever the pane is open. The pane occupies ~40% of the
 * workspace width (clamped 360–640px) as an inline flex child, NOT an
 * overlay drawer.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Form,
  Grid,
  Input,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { NumInput as InputNumber } from "./NumInput";
import {
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";

import {
  ApiError,
  api,
  type ArticleDetail,
  type CategoryRow,
  type ChainRow,
  type ConsumptionSeriesPoint,
  type ProductGroupRow,
  type RecipeRow,
  type StockArticleEntry,
  type VatRateRow,
} from "../api/client";
import { defaultUnitType } from "../lib/recipeUnits";
import { grossToNet } from "../utils/vat";
import { ArticleLedger } from "./ArticleLedger";
import { RecipeSection } from "./catalog/RecipeSection";
import { ConsumptionSparkline } from "./stock/ConsumptionSparkline";
import { PosArticleConfig } from "./pos/PosArticleConfig";
import { PfandEditor } from "./stock/PfandEditor";

const { Title, Text } = Typography;

type PaneMode =
  | { kind: "article-view"; articleId: number }
  | { kind: "article-create"; prefill?: { category?: number; group?: number } }
  | { kind: "category-create" }
  | { kind: "category-edit"; categoryId: number }
  | { kind: "group-create"; prefill?: { category?: number } }
  | { kind: "group-edit"; groupId: number };

interface Props {
  mode: PaneMode;
  /** Catalog rows the workspace already loaded; lets us render the
   *  article-view pane without an extra round trip. */
  rows: ChainRow[];
  categories: CategoryRow[];
  groups: ProductGroupRow[];
  /** Bulk stock map (when fetched) for the article pane's days-of-cover
   *  + last-refill cards. */
  stock?: Map<number, StockArticleEntry>;
  /** chain_root_id → its recipe (if the article is one) — drives the
   *  in-pane recipe editor. */
  recipeByRoot: Map<number, RecipeRow>;
  /** Query string the workspace is currently using; preserved on close. */
  preservedQuery: string;
  /** Invoked after a successful mutation that may have changed the catalog. */
  onChanged: () => void;
}

export const CatalogRightPane = ({
  mode,
  rows,
  categories,
  groups,
  stock,
  recipeByRoot,
  preservedQuery,
  onChanged,
}: Props) => {
  if (mode.kind === "article-view") {
    return (
      <ArticleViewPane
        articleId={mode.articleId}
        rows={rows}
        categories={categories}
        groups={groups}
        stock={stock}
        recipeByRoot={recipeByRoot}
        preservedQuery={preservedQuery}
        onChanged={onChanged}
      />
    );
  }
  if (mode.kind === "article-create") {
    return (
      <ArticleCreatePane
        prefill={mode.prefill}
        categories={categories}
        groups={groups}
        preservedQuery={preservedQuery}
        onChanged={onChanged}
      />
    );
  }
  if (mode.kind === "category-create") {
    return (
      <CategoryCreatePane
        preservedQuery={preservedQuery}
        onChanged={onChanged}
      />
    );
  }
  if (mode.kind === "category-edit") {
    return (
      <CategoryEditPane
        categoryId={mode.categoryId}
        categories={categories}
        rows={rows}
        groups={groups}
        preservedQuery={preservedQuery}
        onChanged={onChanged}
      />
    );
  }
  if (mode.kind === "group-edit") {
    return (
      <GroupEditPane
        groupId={mode.groupId}
        categories={categories}
        groups={groups}
        rows={rows}
        preservedQuery={preservedQuery}
        onChanged={onChanged}
      />
    );
  }
  return (
    <GroupCreatePane
      prefill={mode.prefill}
      categories={categories}
      preservedQuery={preservedQuery}
      onChanged={onChanged}
    />
  );
};

// ---------------------------------------------------------------------------
// Article view + edit
// ---------------------------------------------------------------------------

function ArticleViewPane({
  articleId,
  rows,
  categories,
  groups,
  stock,
  recipeByRoot,
  preservedQuery,
  onChanged,
}: {
  articleId: number;
  rows: ChainRow[];
  categories: CategoryRow[];
  groups: ProductGroupRow[];
  stock?: Map<number, StockArticleEntry>;
  recipeByRoot: Map<number, RecipeRow>;
  preservedQuery: string;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const row = useMemo(
    () => rows.find((r) => r.chain_root_id === articleId) ?? null,
    [rows, articleId],
  );
  // Pickable consumables + a name lookup for the in-pane recipe editor (one
  // entry per active chain root, with its default ingredient unit).
  const recipeConsumables = useMemo(
    () =>
      rows
        .filter((r) => r.active)
        .map((r) => ({
          value: r.chain_root_id,
          label: `${r.name} (#${r.chain_root_id})`,
          unitType: defaultUnitType(r.unit_type, r.unit_type_override),
        })),
    [rows],
  );
  const nameByRoot = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of rows) m.set(r.chain_root_id, r.name);
    return m;
  }, [rows]);
  // Bulk stock entry for days-of-cover + last-refill (present when the
  // workspace fetched the stock map — i.e. the highlight is on or a stock
  // column is visible). Current stock / counted / mismatch ride on the
  // ChainRow itself, so those cards render even without it.
  const stockEntry = stock?.get(articleId) ?? null;
  const [detail, setDetail] = useState<ArticleDetail | null>(null);
  const [series, setSeries] = useState<ConsumptionSeriesPoint[] | null>(null);
  const [vatRates, setVatRates] = useState<VatRateRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Fetch detail (incl. buy_price_history) + 30-day sparkline series on
  // article switch. Both are keyed on the chain root — backend resolves to
  // the correct chain via the strichliste DB so root/leaf/intermediate all
  // work. (Lifetime revenue/units now live in the Ledger section below.)
  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setSeries(null);
    void api
      .getConsumptionSeries(articleId, 30)
      .then((r) => {
        if (!cancelled) setSeries(r.series);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof ApiError ? err.message : String(err));
      });
    // Use the current active leaf id for the detail fetch — chain roots
    // can be inactive after a price change, but the leaf is always in
    // strichliste's active list.
    if (row?.current_leaf_id) {
      void api
        .getArticle(row.current_leaf_id)
        .then((d) => {
          if (!cancelled) setDetail(d);
        })
        .catch((err) => {
          if (!cancelled)
            setError(err instanceof ApiError ? err.message : String(err));
        });
    }
    return () => {
      cancelled = true;
    };
  }, [articleId, row?.current_leaf_id]);

  // VAT rates for the per-article selector (active rows; system Standard/
  // Reduced are always present). Loaded once — the list is tiny + static.
  useEffect(() => {
    void api
      .listVatRates()
      .then(setVatRates)
      .catch(() => setVatRates([]));
  }, []);

  if (!row) {
    return (
      <PaneFrame
        title={`Article #${articleId}`}
        preservedQuery={preservedQuery}
      >
        <Alert type="warning" message="Article not in current catalog snapshot." />
      </PaneFrame>
    );
  }

  const groupsInCategory = groups.filter(
    (g) => g.category_id === row.category_id,
  );

  // Net price is derived from the gross sell price and the article's VAT
  // rate — the site keeps everything gross. ``grossToNet`` is the
  // buchhalterisch helper (commercial rounding; net + vat == gross),
  // shared with the backend's app/vat.py.
  const vatPercent = vatRates.find((v) => v.id === row.vat_rate_id)?.percent;
  const netCents =
    vatPercent != null
      ? grossToNet(row.sell_price_cents, vatPercent).netCents
      : null;

  const onCategoryChange = async (next: number) => {
    setError(null);
    try {
      // Switching category clears the current group (the group might
      // belong to the prior category). The operator picks a new group
      // afterwards if they want one.
      await api.assignArticleGroup(articleId, {
        product_group_id: null,
        category_id: next,
      });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };
  const onGroupChange = async (next: number | null) => {
    setError(null);
    try {
      await api.assignArticleGroup(articleId, {
        product_group_id: next,
        category_id: next === null ? row.category_id : null,
      });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const onRenameArticle = async (next: string) => {
    if (next.trim() === row.name) return;
    setError(null);
    try {
      // PATCH proxies through to strichliste's update flow — if the
      // article has been used, strichliste forks a new id with a
      // precursor chain; the admin's chain root tracking absorbs that.
      await api.updateArticle(row.current_leaf_id, { name: next.trim() });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const onUpdateSellPrice = async (cents: number) => {
    if (cents === row.sell_price_cents) return;
    setError(null);
    try {
      await api.updateArticle(row.current_leaf_id, {
        sell_price_cents: cents,
      });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const onVatChange = async (next: number) => {
    setError(null);
    try {
      await api.assignArticleVat(articleId, next);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };
  // Per-article cup pfand override (three-state). null = inherit category.
  const onCupOverrideChange = async (v: "inherit" | "always" | "never") => {
    setError(null);
    try {
      await api.patchMetadata(row.current_leaf_id, {
        cup_required_override: v === "always" ? true : v === "never" ? false : null,
        set_cup_required_override: true,
      });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <PaneFrame title={row.name} preservedQuery={preservedQuery}>
      {!row.active && (
        <div style={{ marginBottom: 12 }}>
          <Tag
            color="red"
            style={{
              fontSize: 14,
              fontWeight: 700,
              padding: "2px 10px",
              letterSpacing: 0.5,
            }}
          >
            DELETED
          </Tag>
          <Text type="secondary" style={{ marginLeft: 8 }}>
            Hidden from the active catalog; past transactions still
            reference this row. Undelete is not yet wired up.
          </Text>
        </div>
      )}
      {error && (
        <Alert type="error" message={error} showIcon style={{ marginBottom: 12 }} />
      )}

      <Section title="Name">
        <InlineEditableText value={row.name} onSave={onRenameArticle} />
      </Section>

      <Section title="Category & group">
        <Space direction="vertical" style={{ width: "100%" }} size={8}>
          <Select<number>
            value={row.category_id ?? undefined}
            placeholder="Pick a category"
            style={{ width: "100%" }}
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
            onChange={onCategoryChange}
          />
          <Select<number | null>
            value={row.product_group_id ?? null}
            placeholder={row.category_id ? "Ungrouped" : "Pick a category first"}
            style={{ width: "100%" }}
            allowClear
            disabled={!row.category_id}
            options={groupsInCategory.map((g) => ({
              value: g.id,
              label: g.name,
            }))}
            onChange={(v) => void onGroupChange((v as number | null) ?? null)}
          />
        </Space>
      </Section>

      {/* Read-only stock cards folded in from the retired Stock Overview
          right pane. Current-stock is FIRST so a low-stock-highlighted row
          surfaces its on-hand figure immediately. The Pfand editor + 30-day
          consumption sparkline already live below — not duplicated here.
          Refills/stocktakes themselves happen on /stock. */}
      <Section title="Current stock">
        <Space direction="vertical" size={2} style={{ width: "100%" }}>
          <Text>
            On hand:{" "}
            <Text strong type={row.expected_qty < 0 ? "danger" : undefined}>
              {row.expected_qty}
            </Text>
          </Text>
          <Text>
            Days of cover:{" "}
            <Text strong>
              {stockEntry?.days_of_cover != null
                ? `${Math.round(stockEntry.days_of_cover)} d`
                : "—"}
            </Text>
            {recentDocLabel(row.expected_qty, series) && (
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                ({recentDocLabel(row.expected_qty, series)})
              </Text>
            )}
          </Text>
        </Space>
      </Section>

      <Section title="Last stocktake">
        {row.counted_qty === null || row.counted_qty === undefined ? (
          <Text type="secondary">No stocktake yet.</Text>
        ) : (
          <Space direction="vertical" size={2}>
            <Text>
              Last counted: <Text strong>{row.counted_qty}</Text>
            </Text>
            <Text>
              Mismatch:{" "}
              <Text
                strong
                type={
                  (row.mismatch_qty ?? 0) < 0
                    ? "danger"
                    : (row.mismatch_qty ?? 0) > 0
                      ? "warning"
                      : undefined
                }
              >
                {row.mismatch_qty ?? 0}
              </Text>
            </Text>
          </Space>
        )}
      </Section>

      <Section title="Last refill">
        <Text type="secondary">
          {stockEntry?.last_refill_at
            ? formatTimestamp(stockEntry.last_refill_at)
            : "never"}
        </Text>
      </Section>

      <Section title="Pricing">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Text type="secondary">Sell</Text>
          <Space size={6}>
            {netCents !== null && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                ({formatCents(netCents)} net)
              </Text>
            )}
            <InlineEditableCents
              valueCents={row.sell_price_cents}
              onSave={onUpdateSellPrice}
            />
          </Space>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 6,
          }}
        >
          <Text type="secondary">VAT</Text>
          <Select<number>
            size="small"
            value={row.vat_rate_id}
            style={{ minWidth: 170 }}
            options={vatRates.map((v) => ({
              value: v.id,
              label: `${Number(v.percent)}% ${v.name}`,
            }))}
            onChange={(v) => void onVatChange(v)}
          />
        </div>
        <KV
          label="Buy"
          value={formatCents(row.buy_price_cents)}
          caption={
            row.buy_price_cents !== null
              ? "from latest refill"
              : "no refill on record"
          }
        />
        <KV label="Margin" value={formatCents(row.margin_cents)} />
      </Section>

      <Section title="Buy price history">
        {!detail ? (
          <Text type="secondary">Loading…</Text>
        ) : detail.buy_price_history.length === 0 ? (
          <Text type="secondary">No refills recorded for this article.</Text>
        ) : (
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            {detail.buy_price_history.map((h) => (
              <div
                key={h.id}
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <span>
                  <Text>{formatCents(h.buy_price_cents)}</Text>
                  {h.supplier && (
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      ({h.supplier})
                    </Text>
                  )}
                </span>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {h.effective_from.slice(0, 10)}
                  {h.effective_to ? ` → ${h.effective_to.slice(0, 10)}` : " → now"}
                </Text>
              </div>
            ))}
          </Space>
        )}
      </Section>

      <Section title="Unit + size override">
        <UnitOverrideEditor
          articleId={row.current_leaf_id}
          parsedSize={row.unit_size}
          parsedType={row.unit_type}
          parserConfidence={row.parser_confidence}
          overrideSize={row.unit_size_override}
          overrideType={row.unit_type_override}
          onSaved={onChanged}
        />
      </Section>

      <Section title="Refill pack size">
        <PackSizeEditor
          articleId={row.current_leaf_id}
          packSize={row.pack_size}
          unitDescription={
            row.unit_size && row.unit_type
              ? `${Number(row.unit_size_override ?? row.unit_size)} ${row.unit_type_override ?? row.unit_type}`
              : "unit"
          }
          onSaved={onChanged}
        />
      </Section>

      <Section title="Pfand (deposit)">
        <PfandEditor
          chainRootId={row.chain_root_id}
          pfandValueId={row.pfand_value_id}
          cratePfandValueId={row.crate_pfand_value_id}
          onSaved={onChanged}
        />
      </Section>

      <Section title="POS">
        <PosArticleConfig
          chainRootId={row.chain_root_id}
          onsitePfandValueId={row.onsite_pfand_value_id}
          posPriceCents={row.pos_price_cents}
          onSaved={onChanged}
        />
      </Section>

      <Section title="Cup (Becher) pfand">
        <Space direction="vertical" size={6} style={{ width: "100%" }}>
          <Segmented
            value={
              row.cup_required_override === true
                ? "always"
                : row.cup_required_override === false
                  ? "never"
                  : "inherit"
            }
            onChange={(v) =>
              void onCupOverrideChange(v as "inherit" | "always" | "never")
            }
            options={[
              { value: "inherit", label: "Inherit category" },
              { value: "always", label: "Always" },
              { value: "never", label: "Never" },
            ]}
          />
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            Whether selling this article auto-bundles a cup deposit at the POS.
            <b> Inherit</b> follows its category's cup flag; <b>Always</b>/
            <b>Never</b> override it per article (e.g. a single mixed drink in an
            otherwise cup-free category). Configure the cup itself in{" "}
            <b>POS → Cups &amp; deposits</b>.
          </Typography.Text>
        </Space>
      </Section>

      <Section title="Barcodes">
        <BarcodeEditor
          articleId={row.current_leaf_id}
          barcodes={row.barcodes}
          onChanged={onChanged}
        />
      </Section>

      <Section title="Chain history">
        {row.chain.length <= 1 ? (
          <Text type="secondary">No price changes — single article version.</Text>
        ) : (
          <Space direction="vertical" size={6} style={{ width: "100%" }}>
            {/* Order leaf → root so the most recent version reads first. */}
            {[...row.chain]
              .sort((a, b) => {
                const rank = (r: typeof a.role) =>
                  r === "leaf" || r === "root_and_leaf" ? 0 : r === "intermediate" ? 1 : 2;
                return rank(a.role) - rank(b.role);
              })
              .map((c, idx, arr) => {
                // Sort is leaf → root (newest at top). Each version is
                // born by diffing the OLDER neighbour (further down the
                // list) — so the "renamed / repriced" marker lands on
                // the row that resulted from the change, not on the one
                // that was superseded.
                const older = arr[idx + 1];
                const nameChanged = older !== undefined && older.name !== c.name;
                const priceChanged =
                  older !== undefined && older.sell_price_cents !== c.sell_price_cents;
                return (
                  <div
                    key={c.id}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      paddingBottom: 4,
                      borderBottom:
                        idx < arr.length - 1
                          ? "1px dashed var(--ant-color-border, rgba(0,0,0,0.12))"
                          : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <Tag
                          color={
                            c.role === "leaf" || c.role === "root_and_leaf" ? "blue" : "default"
                          }
                        >
                          {c.role}
                        </Tag>
                        <Text strong>{c.name}</Text>
                      </span>
                      <Text type="secondary">{formatCents(c.sell_price_cents)}</Text>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 8,
                        fontSize: 11,
                      }}
                    >
                      <Text type="secondary">
                        id {c.id}
                        {c.created ? ` · ${c.created.slice(0, 10)}` : ""}
                      </Text>
                      {older !== undefined && (nameChanged || priceChanged) && (
                        <Text type="secondary" style={{ fontStyle: "italic" }}>
                          {nameChanged && priceChanged
                            ? "renamed + repriced"
                            : nameChanged
                              ? "renamed"
                              : "repriced"}
                        </Text>
                      )}
                    </div>
                  </div>
                );
              })}
          </Space>
        )}
      </Section>

      <Section title="Consumption (last 30 days)">
        {series === null ? (
          <Text type="secondary">Loading…</Text>
        ) : (
          <ConsumptionSparkline series={series} />
        )}
      </Section>

      <Section title="Recipe">
        <RecipeSection
          chainRootId={articleId}
          recipe={recipeByRoot.get(articleId) ?? null}
          consumables={recipeConsumables}
          nameByRoot={(id) => nameByRoot.get(id) ?? `#${id}`}
          onChanged={onChanged}
        />
      </Section>

      <Section title="Ledger">
        <ArticleLedger articleId={articleId} />
      </Section>

      <div style={{ marginTop: 16 }}>
        <Popconfirm
          title="Delete this article?"
          description={
            <span>
              Strichliste keeps the row + past transactions intact; the
              article is hidden from the active catalog. Undelete is
              not yet wired up — confirm only if you're sure.
            </span>
          }
          okText="Delete"
          okType="danger"
          cancelText="Cancel"
          placement="topRight"
          onConfirm={() =>
            void onDelete(
              // The strichliste DELETE only flips the targeted article's
              // active flag. The chain root is usually a stale precursor
              // (already active=false after a price change), so deleting
              // it is a no-op — the chain still has its live leaf.
              // Target the current leaf so the chain actually goes
              // inactive.
              row.current_leaf_id,
              navigate,
              preservedQuery,
              onChanged,
              setError,
            )
          }
        >
          <Button type="default" danger>
            Delete article
          </Button>
        </Popconfirm>
      </div>
    </PaneFrame>
  );
}

async function onDelete(
  articleId: number,
  navigate: ReturnType<typeof useNavigate>,
  preservedQuery: string,
  onChanged: () => void,
  setError: (msg: string | null) => void,
) {
  setError(null);
  try {
    await api.deleteArticle(articleId);
    onChanged();
    navigate(`/catalog${preservedQuery ? `?${preservedQuery}` : ""}`);
  } catch (err) {
    setError(err instanceof ApiError ? err.message : String(err));
  }
}

// ---------------------------------------------------------------------------
// Article create
// ---------------------------------------------------------------------------

function ArticleCreatePane({
  prefill,
  categories,
  groups,
  preservedQuery,
  onChanged,
}: {
  prefill?: { category?: number; group?: number };
  categories: CategoryRow[];
  groups: ProductGroupRow[];
  preservedQuery: string;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill values from URL query come from the entry affordance
  // (e.g. "+ Add to this group" pre-fills category + group). The
  // selects stay editable — the operator can change either pick
  // without an extra "click to unlock" step.
  const initialCategory = prefill?.category ?? null;
  const initialGroup = prefill?.group ?? null;
  const watchedCategory =
    (Form.useWatch("category_id", form) as number | undefined) ??
    initialCategory ??
    undefined;

  const groupsInCategory = useMemo(
    () =>
      groups.filter(
        (g) =>
          watchedCategory !== undefined && g.category_id === watchedCategory,
      ),
    [groups, watchedCategory],
  );

  const onCancel = () => {
    navigate(`/catalog${preservedQuery ? `?${preservedQuery}` : ""}`);
  };

  const onSave = async () => {
    setError(null);
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        name: values.name as string,
        sell_price_cents: values.sell_price_cents as number,
        category_id: values.category_id as number,
        product_group_id: (values.product_group_id as number | undefined) ?? null,
        barcode: (values.barcode as string | undefined) || undefined,
        unit_size_override:
          values.unit_size_override !== undefined && values.unit_size_override !== null
            ? String(values.unit_size_override)
            : undefined,
        unit_type_override:
          (values.unit_type_override as string | undefined) || undefined,
      };
      const result = await api.createArticle(payload);
      onChanged();
      const qs = preservedQuery ? `?${preservedQuery}` : "";
      navigate(`/catalog/articles/${result.chain_root_id}${qs}`, {
        replace: true,
      });
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else if (err instanceof Error) setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PaneFrame title="New article" hideCloseButton preservedQuery={preservedQuery}>
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          category_id: initialCategory ?? undefined,
          product_group_id: initialGroup ?? undefined,
        }}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input placeholder="Club Mate 0,5L" autoFocus />
        </Form.Item>
        <Form.Item
          label="Sell price (cents)"
          name="sell_price_cents"
          rules={[{ required: true, message: "Sell price is required" }]}
        >
          <InputNumber min={0} style={{ width: "100%" }} addonAfter="ct" />
        </Form.Item>
        <Form.Item
          label="Category"
          name="category_id"
          rules={[{ required: true, message: "Category is required" }]}
        >
          <Select
            placeholder="Pick a category"
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
          />
        </Form.Item>
        <Form.Item label="Product group (optional)" name="product_group_id">
          <Select
            placeholder={watchedCategory ? "Pick a group" : "Pick a category first"}
            disabled={!watchedCategory}
            allowClear
            options={groupsInCategory.map((g) => ({
              value: g.id,
              label: g.name,
            }))}
          />
        </Form.Item>
        <Form.Item label="Barcode (optional)" name="barcode">
          <Input placeholder="4029764001807" />
        </Form.Item>
        <Form.Item label="Unit size override" name="unit_size_override">
          <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item label="Unit type override" name="unit_type_override">
          <Select
            allowClear
            options={[
              { value: "liters", label: "Liters" },
              { value: "grams", label: "Grams" },
              { value: "count", label: "Count" },
            ]}
          />
        </Form.Item>
      </Form>
      <PaneFooter>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" loading={submitting} onClick={() => void onSave()}>
          Save
        </Button>
      </PaneFooter>
    </PaneFrame>
  );
}

// ---------------------------------------------------------------------------
// Category create
// ---------------------------------------------------------------------------

function CategoryCreatePane({
  preservedQuery,
  onChanged,
}: {
  preservedQuery: string;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onCancel = () => {
    navigate(`/catalog${preservedQuery ? `?${preservedQuery}` : ""}`);
  };

  const onSave = async () => {
    setError(null);
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const result = await api.createCategory({
        name: values.name as string,
      });
      onChanged();
      navigate(
        `/catalog/categories/${result.id}${preservedQuery ? `?${preservedQuery}` : ""}`,
        { replace: true },
      );
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else if (err instanceof Error) setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PaneFrame title="New category" hideCloseButton preservedQuery={preservedQuery}>
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      <Form form={form} layout="vertical">
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input placeholder="Energy Drinks" autoFocus />
        </Form.Item>
      </Form>
      <PaneFooter>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" loading={submitting} onClick={() => void onSave()}>
          Save
        </Button>
      </PaneFooter>
    </PaneFrame>
  );
}

// ---------------------------------------------------------------------------
// Group create
// ---------------------------------------------------------------------------

function GroupCreatePane({
  prefill,
  categories,
  preservedQuery,
  onChanged,
}: {
  prefill?: { category?: number };
  categories: CategoryRow[];
  preservedQuery: string;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialCategory = prefill?.category ?? null;

  const onCancel = () => {
    navigate(`/catalog${preservedQuery ? `?${preservedQuery}` : ""}`);
  };

  const onSave = async () => {
    setError(null);
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const result = await api.createProductGroup({
        name: values.name as string,
        category_id: values.category_id as number,
      });
      onChanged();
      navigate(
        `/catalog/groups/${result.id}${preservedQuery ? `?${preservedQuery}` : ""}`,
        { replace: true },
      );
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else if (err instanceof Error) setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PaneFrame title="New group" hideCloseButton preservedQuery={preservedQuery}>
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={{ category_id: initialCategory ?? undefined }}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input placeholder="Pils" autoFocus />
        </Form.Item>
        <Form.Item
          label="Category"
          name="category_id"
          rules={[{ required: true, message: "Category is required" }]}
        >
          <Select
            placeholder="Pick a category"
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
          />
        </Form.Item>
      </Form>
      <PaneFooter>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" loading={submitting} onClick={() => void onSave()}>
          Save
        </Button>
      </PaneFooter>
    </PaneFrame>
  );
}

// ---------------------------------------------------------------------------
// Category edit
// ---------------------------------------------------------------------------

function CategoryEditPane({
  categoryId,
  categories,
  rows,
  groups,
  preservedQuery,
  onChanged,
}: {
  categoryId: number;
  categories: CategoryRow[];
  rows: ChainRow[];
  groups: ProductGroupRow[];
  preservedQuery: string;
  onChanged: () => void;
}) {
  const cat = useMemo(
    () => categories.find((c) => c.id === categoryId) ?? null,
    [categories, categoryId],
  );
  const articlesInCat = useMemo(
    () => rows.filter((r) => r.category_id === categoryId),
    [rows, categoryId],
  );
  const groupsInCat = useMemo(
    () => groups.filter((g) => g.category_id === categoryId),
    [groups, categoryId],
  );
  const [error, setError] = useState<string | null>(null);
  const [savingCup, setSavingCup] = useState(false);
  const [savingVis, setSavingVis] = useState(false);
  // 30-day rollup sparkline (spec §12.2). Aggregated server-side
  // across every article in the category; empty categories render the
  // sparkline's "no data" placeholder.
  const [series, setSeries] = useState<ConsumptionSeriesPoint[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    setSeries(null);
    void api
      .getCategoryConsumptionSeries(categoryId, 30)
      .then((r) => {
        if (!cancelled) setSeries(r.series);
      })
      .catch(() => {
        // Decorative — swallow so a transient strichliste-DB outage
        // doesn't paint the whole pane red.
        if (!cancelled) setSeries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  if (!cat) {
    return (
      <PaneFrame title={`Category #${categoryId}`} preservedQuery={preservedQuery}>
        <Alert type="warning" message="Category not in current catalog snapshot." />
      </PaneFrame>
    );
  }

  const onRename = async (next: string) => {
    if (next === cat.name) return;
    setError(null);
    try {
      await api.updateCategory(cat.id, { name: next });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };
  const onToggleActive = async (active: boolean) => {
    setError(null);
    try {
      await api.updateCategory(cat.id, { active });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };
  const onToggleCupRequired = async (cup_required: boolean) => {
    setError(null);
    setSavingCup(true);
    try {
      await api.updateCategory(cat.id, { cup_required });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSavingCup(false);
    }
  };
  const onToggleHideFromPos = async (hide_from_pos: boolean) => {
    setError(null);
    setSavingVis(true);
    try {
      await api.updateCategory(cat.id, { hide_from_pos });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSavingVis(false);
    }
  };
  const onToggleHideFromKiosk = async (hide_from_kiosk: boolean) => {
    setError(null);
    setSavingVis(true);
    try {
      await api.updateCategory(cat.id, { hide_from_kiosk });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSavingVis(false);
    }
  };
  const onToggleIsMerch = async (is_merch: boolean) => {
    setError(null);
    setSavingVis(true);
    try {
      await api.updateCategory(cat.id, { is_merch });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSavingVis(false);
    }
  };

  return (
    <PaneFrame title={`Category: ${cat.name}`} preservedQuery={preservedQuery}>
      {error && (
        <Alert type="error" message={error} showIcon style={{ marginBottom: 12 }} />
      )}
      <Section title="Name">
        <InlineEditableText value={cat.name} onSave={onRename} />
      </Section>
      <Section title="Status">
        <Space>
          <Tag color={cat.active ? "green" : "default"}>
            {cat.active ? "Active" : "Inactive"}
          </Tag>
          <Button size="small" onClick={() => void onToggleActive(!cat.active)}>
            {cat.active ? "Deactivate" : "Reactivate"}
          </Button>
        </Space>
      </Section>
      <Section title="Sort order">
        <Text type="secondary">
          {cat.sort_order} (drag the category row in the table to reorder)
        </Text>
      </Section>
      <Section title="POS cup">
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Space>
            <Switch
              checked={cat.cup_required}
              loading={savingCup}
              onChange={(v) => void onToggleCupRequired(v)}
            />
            <Text>Auto-bundle a cup at the till</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 11 }}>
            When on, selling any article in this category auto-adds a "Cup out"
            line (a reusable-cup deposit) at the POS — for mixed drinks &
            cocktails. Set which article is the cup + its deposit in{" "}
            <b>POS → Cups &amp; deposits</b>.
          </Text>
        </Space>
      </Section>
      <Section title="Direct-sale visibility">
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Space>
            <Switch
              checked={cat.hide_from_pos}
              loading={savingVis}
              onChange={(v) => void onToggleHideFromPos(v)}
            />
            <Text>Hide from main POS</Text>
          </Space>
          <Space>
            <Switch
              checked={cat.hide_from_kiosk}
              loading={savingVis}
              onChange={(v) => void onToggleHideFromKiosk(v)}
            />
            <Text>Hide from kiosk</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 11 }}>
            For <b>filler ingredients</b> (mixers, syrups) you stock and use
            inside recipes / custom drinks but don't sell directly: hiding the
            category keeps its articles off the POS sell screen and/or the kiosk
            while they stay usable as recipe ingredients and countable at
            stocktake.
          </Text>
        </Space>
      </Section>
      <Section title="Merch (Textilien)">
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Space>
            <Switch
              checked={cat.is_merch}
              loading={savingVis}
              onChange={(v) => void onToggleIsMerch(v)}
            />
            <Text>Roll up under Merch on invoices</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 11 }}>
            When on, articles in this category count toward the{" "}
            <b>davon Merch (Textilien)</b> line on settlement invoices —
            a cross-cut of the VAT breakdown (a t-shirt at 19% and a jute
            bag at 7% both roll up together). Leave off for consumables.
          </Text>
        </Space>
      </Section>
      <Section title="Contents">
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Text>
            {articlesInCat.length} article{articlesInCat.length === 1 ? "" : "s"}
            {" · "}
            {groupsInCat.length} group{groupsInCat.length === 1 ? "" : "s"}
          </Text>
          {groupsInCat.length > 0 && (
            <Space size={4} wrap>
              {groupsInCat.map((g) => (
                <Tag key={g.id}>
                  {g.name} ({g.member_count})
                </Tag>
              ))}
            </Space>
          )}
        </Space>
      </Section>
      <Section title="Consumption (last 30 days)">
        {series === null ? (
          <Text type="secondary">Loading…</Text>
        ) : (
          <ConsumptionSparkline series={series} />
        )}
      </Section>
      <Section title="Metrics (placeholder)">
        <Text type="secondary" style={{ fontSize: 12 }}>
          Per-category revenue / profit / units will land here next — the
          backend already returns them via{" "}
          <code>GET /api/catalog/revenue</code>.
        </Text>
      </Section>
    </PaneFrame>
  );
}

// ---------------------------------------------------------------------------
// Group edit
// ---------------------------------------------------------------------------

function GroupEditPane({
  groupId,
  categories,
  groups,
  rows,
  preservedQuery,
  onChanged,
}: {
  groupId: number;
  categories: CategoryRow[];
  groups: ProductGroupRow[];
  rows: ChainRow[];
  preservedQuery: string;
  onChanged: () => void;
}) {
  const group = useMemo(
    () => groups.find((g) => g.id === groupId) ?? null,
    [groups, groupId],
  );
  const articlesInGroup = useMemo(
    () => rows.filter((r) => r.product_group_id === groupId),
    [rows, groupId],
  );
  const [error, setError] = useState<string | null>(null);
  // 30-day rollup sparkline (spec §12.2). Same shape as the article
  // and category panes; backend sums daily qty across every article
  // whose ``product_group_id`` matches.
  const [series, setSeries] = useState<ConsumptionSeriesPoint[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    setSeries(null);
    void api
      .getGroupConsumptionSeries(groupId, 30)
      .then((r) => {
        if (!cancelled) setSeries(r.series);
      })
      .catch(() => {
        if (!cancelled) setSeries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  if (!group) {
    return (
      <PaneFrame title={`Group #${groupId}`} preservedQuery={preservedQuery}>
        <Alert type="warning" message="Group not in current catalog snapshot." />
      </PaneFrame>
    );
  }

  const onRename = async (next: string) => {
    if (next === group.name) return;
    setError(null);
    try {
      await api.updateProductGroup(group.id, { name: next });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };
  const onMoveCategory = async (newCategoryId: number) => {
    setError(null);
    try {
      await api.updateProductGroup(group.id, { category_id: newCategoryId });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };
  const onToggleActive = async (active: boolean) => {
    setError(null);
    try {
      await api.updateProductGroup(group.id, { active });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <PaneFrame title={`Group: ${group.name}`} preservedQuery={preservedQuery}>
      {error && (
        <Alert type="error" message={error} showIcon style={{ marginBottom: 12 }} />
      )}
      <Section title="Name">
        <InlineEditableText value={group.name} onSave={onRename} />
      </Section>
      <Section title="Category">
        {/* Moving a group across categories also moves every article in
            it — the backend cascades category_id on the contained
            article_metadata rows in the same transaction. */}
        <Select<number>
          value={group.category_id}
          style={{ width: "100%" }}
          options={categories.map((c) => ({ value: c.id, label: c.name }))}
          onChange={onMoveCategory}
        />
        <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: "block" }}>
          Changing the category here also moves every article in the
          group to the new category.
        </Text>
      </Section>
      <Section title="Status">
        <Space>
          <Tag color={group.active ? "green" : "default"}>
            {group.active ? "Active" : "Inactive"}
          </Tag>
          <Button size="small" onClick={() => void onToggleActive(!group.active)}>
            {group.active ? "Deactivate" : "Reactivate"}
          </Button>
        </Space>
      </Section>
      <Section title="Sort order">
        <Text type="secondary">
          {group.sort_order} (drag the group row in the table to reorder
          within its category)
        </Text>
      </Section>
      <Section title="Contents">
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Text>
            {articlesInGroup.length} article
            {articlesInGroup.length === 1 ? "" : "s"}
            {group.unit_type_warning && (
              <Tag color="orange" style={{ marginLeft: 8 }}>
                mixed units
              </Tag>
            )}
          </Text>
          {articlesInGroup.length > 0 && (
            <Space size={4} wrap>
              {articlesInGroup.map((a) => (
                <Tag key={a.chain_root_id}>{a.name}</Tag>
              ))}
            </Space>
          )}
        </Space>
      </Section>
      <Section title="Consumption (last 30 days)">
        {series === null ? (
          <Text type="secondary">Loading…</Text>
        ) : (
          <ConsumptionSparkline series={series} />
        )}
      </Section>
      <Section title="Metrics (placeholder)">
        <Text type="secondary" style={{ fontSize: 12 }}>
          Per-group revenue / profit / units will land here next.
        </Text>
      </Section>
    </PaneFrame>
  );
}

// ---------------------------------------------------------------------------
// Shared pane chrome
// ---------------------------------------------------------------------------

// Width clamp for the right pane. The 360px floor keeps the form
// usable; the 900px ceiling keeps the catalog table from being
// squashed below its useful column count.
const PANE_MIN = 360;
const PANE_MAX = 900;
const PANE_DEFAULT = 460;
const PANE_STORAGE_KEY = "catalog.rightPaneWidth";

function readStoredPaneWidth(): number {
  try {
    const raw = window.localStorage.getItem(PANE_STORAGE_KEY);
    if (!raw) return PANE_DEFAULT;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return PANE_DEFAULT;
    return Math.max(PANE_MIN, Math.min(PANE_MAX, n));
  } catch {
    return PANE_DEFAULT;
  }
}

function PaneFrame({
  title,
  children,
  hideCloseButton,
  preservedQuery,
}: {
  title: string;
  children: React.ReactNode;
  hideCloseButton?: boolean;
  preservedQuery: string;
}) {
  const navigate = useNavigate();
  const [width, setWidth] = useState(() => readStoredPaneWidth());
  // Pane fills the full row when the parent workspace hides the
  // catalog. We can't easily share state with the workspace from here,
  // so we infer the same condition: narrow viewport OR the parent has
  // collapsed our flex sibling. Detection is via ``offsetWidth`` of
  // our own DOM node — when the workspace switches us to ``flex: 1 1
  // auto``, our measured width matches the parent's; when it's the
  // resizable two-pane mode, our width is the resized value (≤ 900).
  // Using ``screens.md`` as the initial guess avoids a one-frame flash
  // of resize-grip on phones; the runtime measurement below refines.
  const screens = Grid.useBreakpoint();
  const paneRef = useRef<HTMLDivElement | null>(null);
  const [measuredFullWidth, setMeasuredFullWidth] = useState(false);
  useEffect(() => {
    const el = paneRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const update = () => {
      const parentW = el.parentElement?.getBoundingClientRect().width ?? 0;
      const ownW = el.getBoundingClientRect().width;
      // If we take up the entire parent row (within 2px slop), we're in
      // the catalog-hidden full-width mode. Otherwise we're side-by-side.
      setMeasuredFullWidth(parentW > 0 && ownW >= parentW - 2);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    return () => ro.disconnect();
  }, []);
  const fullWidth = !screens.md || measuredFullWidth;

  // Drag-to-resize via pointer events on the left-edge grip. Document-
  // level listeners stay alive while dragging so the resize keeps
  // tracking the cursor even if it leaves the grip element. We also
  // listen for ``pointercancel`` (browser-initiated abort, e.g. touch
  // scroll wins the gesture) and store a teardown function in a ref so
  // an unmount mid-drag can clean up document listeners + body styles.
  const resizeTeardownRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    // Component-unmount safety net: if the user navigates away while a
    // resize gesture is still in flight, the listeners would otherwise
    // leak and the body cursor / user-select would stay stuck.
    return () => resizeTeardownRef.current?.();
  }, []);
  const onResizeStart = (startEvent: React.PointerEvent) => {
    startEvent.preventDefault();
    const startX = startEvent.clientX;
    const startWidth = width;
    const onMove = (e: PointerEvent) => {
      // Pane is anchored to the right; dragging left grows the width.
      const delta = startX - e.clientX;
      const next = Math.max(PANE_MIN, Math.min(PANE_MAX, startWidth + delta));
      setWidth(next);
    };
    const stop = (persist: boolean, finalX?: number) => {
      if (persist && finalX !== undefined) {
        const delta = startX - finalX;
        const final = Math.max(PANE_MIN, Math.min(PANE_MAX, startWidth + delta));
        try {
          window.localStorage.setItem(PANE_STORAGE_KEY, String(final));
        } catch {
          // ignore — resizing still works in-session, just won't persist
        }
      }
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onCancel);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      resizeTeardownRef.current = null;
    };
    const onUp = (e: PointerEvent) => stop(true, e.clientX);
    const onCancel = () => stop(false);
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onCancel);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    resizeTeardownRef.current = () => stop(false);
  };

  return (
    <div
      ref={paneRef}
      style={{
        // Sibling of the catalog's scroll container inside a flex
        // workspace bounded to the viewport-minus-chrome height (set on
        // the workspace outer div). We just take the full
        // height of that bounded box and scroll internally — no
        // sticky needed because the workspace itself doesn't overflow
        // the viewport. This also keeps the catalog from forcing a
        // page-level scrollbar when the pane is taller than the table.
        position: "relative",
        display: "flex",
        flexDirection: "column",
        // ``flex: 1 1 auto`` lets the pane fill the row when the catalog
        // is hidden (mobile single-pane mode). Otherwise stick with the
        // resized width so the resize grip works as before.
        flex: fullWidth ? "1 1 auto" : `0 0 ${width}px`,
        minWidth: fullWidth ? 0 : PANE_MIN,
        maxWidth: fullWidth ? "none" : PANE_MAX,
        borderLeft: fullWidth
          ? "none"
          : "1px solid var(--ant-color-border-secondary, rgba(0,0,0,0.06))",
        height: "100%",
        // No padding/overflow here — the inner wrapper below scrolls, so the
        // resize grip (a sibling of that wrapper) is neither clipped by
        // overflow-x:auto nor scrolled away with the content.
      }}
    >
      {/* Resize grip — a 14px hot-zone hugging the pane's left edge, kept
          OUTSIDE the scrolling content wrapper below so it is neither clipped
          by overflow-x:auto nor scrolled away. A centred rounded bar makes it
          discoverable; the whole zone tints on hover. Hidden in full-width
          (mobile) mode — nothing to resize against. */}
      {!fullWidth && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize detail pane"
          onPointerDown={onResizeStart}
          style={{
            position: "absolute",
            left: -7,
            top: 0,
            bottom: 0,
            width: 14,
            cursor: "col-resize",
            zIndex: 5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            touchAction: "none",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background =
              "var(--ant-color-primary-bg, rgba(22,119,255,0.18))")
          }
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <div
            style={{
              width: 4,
              height: 44,
              borderRadius: 3,
              background: "var(--ant-color-text-quaternary, rgba(0,0,0,0.28))",
            }}
          />
        </div>
      )}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: 16,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
        <Title level={4} style={{ margin: 0 }}>
          {title}
        </Title>
        {!hideCloseButton && (
          <Tooltip title="Close (preserves sort + filter + window)">
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() =>
                navigate(
                  `/catalog${preservedQuery ? `?${preservedQuery}` : ""}`,
                )
              }
              aria-label="Close detail pane"
            />
          </Tooltip>
        )}
        </div>
        {children}
      </div>
    </div>
  );
}

function PaneFooter({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: "auto",
        paddingTop: 16,
        display: "flex",
        justifyContent: "flex-end",
        gap: 8,
      }}
    >
      {children}
    </div>
  );
}

function BarcodeEditor({
  articleId,
  barcodes,
  onChanged,
}: {
  articleId: number;
  barcodes: { id: number; barcode: string }[];
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addOne = async () => {
    const value = draft.trim();
    if (!value) return;
    setBusy(true);
    setError(null);
    try {
      await api.addBarcode(articleId, value);
      setDraft("");
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (barcodeId: number) => {
    setBusy(true);
    setError(null);
    try {
      await api.removeBarcode(articleId, barcodeId);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Space direction="vertical" size={6} style={{ width: "100%" }}>
      {barcodes.length > 0 && (
        <Space size={6} wrap>
          {barcodes.map((b) => (
            <Tag
              key={b.id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                paddingInlineEnd: 2,
              }}
            >
              <span style={{ fontFamily: "monospace" }}>{b.barcode}</span>
              <Popconfirm
                title="Delete this barcode?"
                description={
                  <span style={{ fontFamily: "monospace" }}>{b.barcode}</span>
                }
                okText="Delete"
                okType="danger"
                cancelText="Cancel"
                onConfirm={() => void removeOne(b.id)}
              >
                <Tooltip title="Delete">
                  <Button
                    size="small"
                    type="text"
                    danger
                    disabled={busy}
                    aria-label={`Delete barcode ${b.barcode}`}
                    icon={<CloseOutlined />}
                    style={{
                      width: 20,
                      height: 20,
                      minWidth: 0,
                      padding: 0,
                      background:
                        "var(--ant-color-error-bg, rgba(255,77,79,0.12))",
                      color: "var(--ant-color-error, #ff4d4f)",
                      borderRadius: 4,
                    }}
                  />
                </Tooltip>
              </Popconfirm>
            </Tag>
          ))}
        </Space>
      )}
      <Space.Compact style={{ width: "100%", maxWidth: 320 }}>
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={() => void addOne()}
          placeholder="Add barcode"
          disabled={busy}
          style={{ fontFamily: "monospace" }}
        />
        <Tooltip title="Add barcode">
          <Button
            type="primary"
            icon={<CheckOutlined />}
            disabled={busy || !draft.trim()}
            onClick={() => void addOne()}
            aria-label="Add barcode"
            style={{
              background:
                "var(--ant-color-success, #52c41a)",
              borderColor: "var(--ant-color-success, #52c41a)",
            }}
          />
        </Tooltip>
      </Space.Compact>
      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
        />
      )}
    </Space>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Text strong style={{ display: "block", marginBottom: 6 }}>
        {title}
      </Text>
      {children}
    </div>
  );
}

function KV({
  label,
  value,
  caption,
}: {
  label: string;
  value: string;
  caption?: string;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
      <Text type="secondary">{label}</Text>
      <span style={{ textAlign: "right" }}>
        <Text>{value}</Text>
        {caption && (
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {caption}
            </Text>
          </div>
        )}
      </span>
    </div>
  );
}

/**
 * Always-visible text editor. The input is shown all the time (no
 * click-to-reveal); the operator can tab through, edit, and the value
 * commits on blur or Enter. We commit only on actual change so a casual
 * focus + blur doesn't trip the strichliste price-change flow.
 */
function InlineEditableText({
  value,
  onSave,
}: {
  value: string;
  onSave: (next: string) => void | Promise<void>;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <Input
      size="middle"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft.trim() !== value) void onSave(draft.trim());
      }}
      onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
    />
  );
}

function InlineEditableCents({
  valueCents,
  onSave,
}: {
  valueCents: number;
  onSave: (cents: number) => void | Promise<void>;
}) {
  const [draft, setDraft] = useState<number>(valueCents);
  useEffect(() => setDraft(valueCents), [valueCents]);
  return (
    <InputNumber
      size="small"
      value={draft}
      onChange={(v) => setDraft(typeof v === "number" ? v : 0)}
      step={1}
      min={0}
      precision={0}
      addonAfter="ct"
      onBlur={() => {
        const cents = Math.round(draft);
        if (cents !== valueCents) void onSave(cents);
      }}
      onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
      style={{ width: 130 }}
    />
  );
}

function formatCents(c: number | null): string {
  if (c === null || c === undefined) return "—";
  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(c / 100);
}

/** ``DD.MM HH:MM`` timestamp for the Last-refill card. */
function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Recent-pace days-of-cover from the last 7 days of the consumption
 * series — a cross-check on the long-window days-of-cover number.
 * Returns null when there isn't enough signal (no stock, <0.5/day pace).
 */
function recentDocLabel(
  currentStock: number | null | undefined,
  series: ConsumptionSeriesPoint[] | null,
): string | null {
  if (currentStock === null || currentStock === undefined) return null;
  if (!series || series.length === 0) return null;
  const tail = series.slice(-7);
  if (tail.length === 0) return null;
  const perDay = tail.reduce((acc, p) => acc + (p.qty ?? 0), 0) / tail.length;
  if (perDay < 0.5) return null;
  if (currentStock <= 0) return "out of stock";
  return `~${Math.round(currentStock / perDay)} d at 7-day pace`;
}

function PackSizeEditor({
  articleId,
  packSize,
  unitDescription,
  onSaved,
}: {
  articleId: number;
  packSize: number | null;
  unitDescription: string;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<number | null>(packSize);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => setDraft(packSize), [packSize]);
  const dirty = draft !== packSize;

  const onSave = async () => {
    setErr(null);
    setSaving(true);
    try {
      await api.patchMetadata(articleId, {
        pack_size: draft && draft > 0 ? draft : null,
        set_pack_size: true,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size={6} style={{ width: "100%" }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        How many {unitDescription} per refill pack? Leave blank for "no
        pack convention — refilled per unit".
      </Text>
      <Space>
        <InputNumber
          placeholder="e.g. 24"
          min={1}
          step={1}
          value={draft ?? undefined}
          onChange={(v) =>
            setDraft(v === null || v === undefined ? null : Math.round(Number(v)))
          }
          style={{ width: 120 }}
        />
        {draft !== null && draft > 1 && (
          <Text type="secondary">
            = {draft} × {unitDescription} per pack
          </Text>
        )}
        <Button
          type="primary"
          size="small"
          disabled={!dirty}
          loading={saving}
          onClick={() => void onSave()}
        >
          Save
        </Button>
        {packSize !== null && draft !== null && (
          <Button
            type="link"
            size="small"
            onClick={() => setDraft(null)}
          >
            Clear
          </Button>
        )}
      </Space>
      {err && <Alert type="error" message={err} showIcon />}
    </Space>
  );
}

function UnitOverrideEditor({
  articleId,
  parsedSize,
  parsedType,
  parserConfidence,
  overrideSize,
  overrideType,
  onSaved,
}: {
  articleId: number;
  parsedSize: string | null;
  parsedType: string | null;
  parserConfidence: string | null;
  overrideSize: string | null;
  overrideType: string | null;
  onSaved: () => void;
}) {
  const [sizeDraft, setSizeDraft] = useState<string | null>(overrideSize);
  const [typeDraft, setTypeDraft] = useState<string | null>(overrideType);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setSizeDraft(overrideSize);
    setTypeDraft(overrideType);
  }, [overrideSize, overrideType]);

  const overrideActive = overrideSize !== null || overrideType !== null;
  const reviewBadge =
    parserConfidence === "low" || parserConfidence === "ambiguous";
  const dirty = sizeDraft !== overrideSize || typeDraft !== overrideType;

  const onSave = async () => {
    setErr(null);
    setSaving(true);
    try {
      await api.patchMetadata(articleId, {
        unit_size_override: sizeDraft ?? undefined,
        unit_type_override: typeDraft ?? undefined,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Text type="secondary">
          Parsed: {parsedSize ?? "—"} {parsedType ?? ""}
        </Text>
        {parserConfidence && (
          <Tag
            color={
              parserConfidence === "high"
                ? "green"
                : parserConfidence === "low"
                  ? "orange"
                  : "gold"
            }
          >
            {parserConfidence}
          </Tag>
        )}
      </div>
      {reviewBadge && (
        <Alert
          type="warning"
          message="Review parsing — confirm or override"
          showIcon
        />
      )}
      <Space style={{ width: "100%" }}>
        <InputNumber
          placeholder="Size"
          value={sizeDraft !== null ? Number(sizeDraft) : undefined}
          min={0}
          step={0.1}
          onChange={(v) => setSizeDraft(v === null || v === undefined ? null : String(v))}
        />
        <Select<string>
          placeholder="Type"
          value={typeDraft ?? undefined}
          style={{ width: 120 }}
          allowClear
          onChange={(v) => setTypeDraft((v as string | undefined) ?? null)}
          options={[
            { value: "liters", label: "Liters" },
            { value: "grams", label: "Grams" },
            { value: "count", label: "Count" },
          ]}
        />
        <Button
          type="primary"
          size="small"
          disabled={!dirty}
          loading={saving}
          onClick={() => void onSave()}
        >
          Save
        </Button>
      </Space>
      {overrideActive && <Tag color="purple">override active</Tag>}
      {err && <Alert type="error" message={err} showIcon />}
    </Space>
  );
}

